import { Injectable } from '@angular/core';
import { type ApolloClient, ApolloLink, InMemoryCache, split } from '@apollo/client/core';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { environment } from '@environments/environment';
import { SessionStorageService } from '@shared/services/storage.service';
import { HttpLink } from 'apollo-angular/http';
import { Kind } from 'graphql';

/*
 * Apollo Client 4.2+ requires default options such as `errorPolicy` to be declared
 * at the type level via module augmentation before they can be set in `defaultOptions`.
 * This keeps the reported types in sync with the runtime behavior (data becomes
 * `T | undefined` when `errorPolicy: 'all'` is used).
 * See https://www.apollographql.com/docs/react/data/typescript#declaring-default-options-for-type-safety
 */
/* eslint-disable ts/no-namespace -- Apollo Client's default-option type API can only be augmented via namespaces */
declare module '@apollo/client' {
	namespace ApolloClient {
		namespace DeclareDefaultOptions {
			interface WatchQuery {
				errorPolicy: 'all';
			}
			interface Query {
				errorPolicy: 'all';
			}
			interface Mutate {
				errorPolicy: 'all';
			}
		}
	}
}
/* eslint-enable ts/no-namespace */

/*
 * This class provides the ApolloClientOptions for the ApolloClient.
 * The ApolloClientOptions are used to create the ApolloClient instance.
 * The ApolloClient instance is used to send queries and mutations to the AppSync API (GraphQL Proxy).
 *
 * Beacuse the AppSync API supports both HTTP and WebSocket protocols, we need to provide the ApolloClient with the appropriate links.
 * However, apollo-angular does not support the mesaage type 'start_ack' which is sent by AppSync after a subscription is started,
 * causing the Apollo client to throw an error when it receives the 'data' message of type 'start_ack' from Appsync.
 * To fix this, we need to monkey-patch the SubscriptionClient to handle the 'start_ack' message.
 */

@Injectable({
	providedIn: 'root',
})
export class GraphQLProvider {
	private HTTP_API_URL = environment.graphqlEndpoint;
	private REALTIME_API_URL = `${this.HTTP_API_URL.replace('appsync-api', 'appsync-realtime-api')}/realtime`;
	private HOST = this.HTTP_API_URL.replace('https://', '').replace('/graphql', '');

	constructor(
		private httpLink: HttpLink,
		private sessionStorageService: SessionStorageService,
	) {}

	private getAuthorizationToken(): string | null {
		return this.sessionStorageService.get('User.Authorization');
	}

	private getAccountId(): string | null {
		const account = this.sessionStorageService.get('User.Account');
		return account ? JSON.parse(account).id : null;
	}

	private getHttpHeaders() {
		return {
			'x-account-id': this.getAccountId(),
			Authorization: this.getAuthorizationToken(),
		};
	}

	private getWebSocketHeaders() {
		return {
			host: this.HOST,
			'x-account-id': this.getAccountId(),
			Authorization: this.getAuthorizationToken(),
		};
	}

	private getWebSocketURL(): string {
		const headers = btoa(
			JSON.stringify({
				host: this.HOST,
				Authorization: this.getAuthorizationToken() || '',
			}),
		);
		const payload = btoa(JSON.stringify({}));
		return `${this.REALTIME_API_URL}?header=${headers}&payload=${payload}`;
	}

	public apolloOptionsFactory(httpLink: HttpLink): ApolloClient.Options {
		const http = httpLink.create({
			uri: this.HTTP_API_URL, // AppSync URL
		});
		let ws: WebSocketLink;
		const createWebSocketLink = (): WebSocketLink => {
			if (ws !== undefined) {
				return ws;
			}
			const wsLink = new WebSocketLink({
				uri: this.getWebSocketURL(), // AppSync WebSocket URL
				options: {
					reconnect: true,
					connectionCallback(error) {
						if (error) {
							console.error('WebSocket error:', error);
						}
					},
				},
			});
			// eslint-disable-next-line ts/no-use-before-define
			patchSubscriptionClient(wsLink);
			return wsLink;
		};

		/**
		 * The way Apollo treats the received message from Appsync is missing the message type 'start_ack' which is sent by Appsync after a subscription is started. This causes the Apollo client to throw an error when it receives the 'data' message of type 'start_ack' from Appsync. To fix this, we need to monkey-patch
		 * the SubscriptionClient to handle the 'start_ack' message.
		 *
		 * Monkey-patch the SubscriptionClient after WebSocketLink creates it
		 */
		const patchSubscriptionClient = (ws: WebSocketLink) => {
			// Get the internal SubscriptionClient from the WebSocketLink instance
			const wsClient = (ws as any).subscriptionClient;
			// Check if SubscriptionClient is available
			if (wsClient) {
				// Save the original method
				const originalProcessReceivedData = wsClient.processReceivedData;
				// Monkey-patch processReceivedData
				wsClient.processReceivedData = function (receivedData: any) {
					let parsedMessage: any = null;
					try {
						parsedMessage = JSON.parse(receivedData);
					} catch {
						/* ignore non-json frames */
					}

					// Align client's keep-alive timeout with server's advertised connectionTimeoutMs (+ buffer) to avoid periodic disconnects
					if (parsedMessage?.type === 'connection_ack' && parsedMessage?.payload?.connectionTimeoutMs) {
						// Add a small buffer so the client does not cut exactly at server boundary
						this.wsTimeout = parsedMessage.payload.connectionTimeoutMs + 15000;
					}

					// Call the original method if the message is not a start_ack
					if (parsedMessage?.type !== 'start_ack' && originalProcessReceivedData) {
						originalProcessReceivedData.call(this, receivedData);
					}
				};
			}
		};

		// Split the links, directing subscriptions to the WebSocket link
		const link = split(
			({ query }) => {
				const definition = getMainDefinition(query);
				return definition.kind === Kind.OPERATION_DEFINITION && definition.operation === 'subscription';
			},
			createWebSocketLink(),
			http,
		);

		// This is called before every request is sent
		const middlewareLink = new ApolloLink((operation, forward) => {
			// auth mode
			operation.extensions = {
				...operation.extensions,
				authorization: { ...this.getWebSocketHeaders() },
			};

			// fix data payload (HERES THE HOPE)
			Object.assign(operation, {
				data: JSON.stringify({
					query: operation.query.loc?.source.body,
					variables: operation.variables,
				}),
			});

			// http request
			operation.setContext({
				headers: {
					...this.getHttpHeaders(),
				},
			});

			return forward(operation);
		});

		if (!environment.production) {
			loadDevMessages();
			loadErrorMessages();
		}

		return {
			link: ApolloLink.from([middlewareLink, link]),
			cache: new InMemoryCache(),
			defaultOptions: {
				watchQuery: {
					fetchPolicy: 'no-cache',
					errorPolicy: 'all',
				},
				query: {
					fetchPolicy: 'no-cache',
					errorPolicy: 'all',
				},
				mutate: {
					fetchPolicy: 'no-cache',
					errorPolicy: 'all',
				},
			},
		};
	}
}
